import { useState } from "react";

import { Button } from "@swot/base-components";

import { ApiErrorPanel } from "@components/errors/index.js";
import { useApiError } from "@hooks/useApiError.js";
import { reportApi } from "@services/reportApi.js";

import {
  CmtsReportDownloadButton,
} from "./CmtsReportDownloadButton.js";

type ExportJob = Record<string, unknown>;
type ExportFormat = "CSV" | "XLSX" | "JSON";

export type CmtsReportExportPanelProps = {
  readonly reportCode: string;
  readonly filters?:
    | Record<string, unknown>
    | undefined;
  readonly canExport?: boolean | undefined;
};

const exportFormats: readonly {
  readonly format: ExportFormat;
  readonly title: string;
  readonly description: string;
}[] = [
  {
    format: "CSV",
    title: "CSV Spreadsheet",
    description:
      "Comma-separated data for Excel and bulk processing.",
  },
  {
    format: "XLSX",
    title: "Excel Workbook",
    description:
      "Formatted Microsoft Excel workbook for business users.",
  },
  {
    format: "JSON",
    title: "JSON Data",
    description:
      "Structured report data for systems and integrations.",
  },
];

export function CmtsReportExportPanel(
  props: CmtsReportExportPanelProps,
) {
  const [pendingFormat, setPendingFormat] =
    useState<ExportFormat | null>(null);

  const [selectedFormat, setSelectedFormat] =
    useState<ExportFormat | null>(null);

  const [job, setJob] =
    useState<ExportJob | null>(null);

  const apiError = useApiError();

  const pending = pendingFormat !== null;

  async function request(
    format: ExportFormat,
  ): Promise<void> {
    if (
      pending ||
      props.canExport === false ||
      !props.reportCode.trim()
    ) {
      return;
    }

    setPendingFormat(format);
    setSelectedFormat(format);
    setJob(null);
    apiError.clearError();

    try {
      const result =
        await reportApi.requestReportExport(
          props.reportCode,
          {
            reportCode: props.reportCode,
            filters: props.filters ?? {},
            format,
            exportFormat: format,
            requestedAt: new Date().toISOString(),
          },
        );

      setJob(result);
    } catch (error: unknown) {
      apiError.setError(error, {
        operation: "report.export",
        entityType: "Report",
        entityId: props.reportCode,
      });
    } finally {
      setPendingFormat(null);
    }
  }

  const exportId = stringValue(
    job?.exportId ?? job?.id,
  );

  const status = stringValue(
    job?.status ??
    job?.exportStatus ??
    job?.state ??
    (job ? "ACCEPTED" : ""),
  );

  const correlationId = stringValue(
    job?.correlationId ??
    job?.auditId ??
    "Not returned",
  );

  const filename = buildExportFilename(
    props.reportCode,
    selectedFormat,
    job,
  );

  return (
    <section className="cmts-report-export-panel">
      <header className="cmts-report-panel-header">
        <div>
          <span className="cmts-report-panel-eyebrow">
            SECURE REPORT EXPORT
          </span>

          <h2 className="cmts-report-panel-title">
            Export {formatReportCode(props.reportCode)}
          </h2>

          <p className="cmts-report-panel-description">
            Select a file format. The report will be generated
            using your current tenant, permissions and filters.
          </p>
        </div>

        <Button
          label="Print preview"
          variant="secondary"
          disabled={pending}
          onClick={() => window.print()}
        />
      </header>

      {props.canExport === false ? (
        <div className="cmts-report-export-warning">
          You do not have permission to export this report.
        </div>
      ) : null}

      <div
        className="cmts-report-export-formats"
        aria-label="Report export formats"
      >
        {exportFormats.map((item) => (
          <button
            key={item.format}
            type="button"
            className={[
              "cmts-report-format-card",
              selectedFormat === item.format
                ? "cmts-report-format-card--active"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            disabled={
              pending ||
              props.canExport === false
            }
            onClick={() => void request(item.format)}
          >
            <span className="cmts-report-format-badge">
              {pendingFormat === item.format
                ? "..."
                : item.format}
            </span>

            <strong>{item.title}</strong>

            <small>{item.description}</small>
          </button>
        ))}
      </div>

      <ApiErrorPanel
        error={apiError.error}
        compact
        onRetry={
          selectedFormat
            ? () => void request(selectedFormat)
            : undefined
        }
      />

      {job ? (
        <div className="cmts-report-export-result">
          <div className="cmts-report-export-success-icon">
            ✓
          </div>

          <div className="cmts-report-export-result-content">
            <strong>
              {selectedFormat} export request accepted
            </strong>

            <div className="cmts-report-export-result-grid">
              <span>Export ID</span>
              <strong>{exportId || "Not returned"}</strong>

              <span>Format</span>
              <strong>{selectedFormat ?? "Not returned"}</strong>

              <span>Status</span>
              <strong>{status || "ACCEPTED"}</strong>

              <span>Filename</span>
              <strong>{filename}</strong>

              <span>Correlation</span>
              <strong>{correlationId}</strong>
            </div>

            {exportId ? (
              <CmtsReportDownloadButton
                exportId={exportId}
                filename={filename}
              />
            ) : (
              <p className="cmts-report-export-pending-message">
                Backend accepted the export but did not return
                an export ID. Check the export-history screen.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function buildExportFilename(
  reportCode: string,
  format: ExportFormat | null,
  job: ExportJob | null,
): string {
  const backendFilename =
    job?.fileName ??
    job?.filename;

  if (
    typeof backendFilename === "string" &&
    backendFilename.trim()
  ) {
    return ensureFileExtension(
      backendFilename.trim(),
      format,
    );
  }

  const safeReportCode = reportCode
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return `${
    safeReportCode || "cmts-report"
  }-${createDateStamp()}.${extensionOf(format)}`;
}

function ensureFileExtension(
  filename: string,
  format: ExportFormat | null,
): string {
  const expectedExtension = extensionOf(format);

  if (
    filename
      .toLowerCase()
      .endsWith(`.${expectedExtension}`)
  ) {
    return filename;
  }

  const withoutExtension =
    filename.replace(/\.[a-z0-9]+$/i, "");

  return `${withoutExtension}.${expectedExtension}`;
}

function extensionOf(
  format: ExportFormat | null,
): string {
  switch (format) {
    case "CSV":
      return "csv";
    case "XLSX":
      return "xlsx";
    case "JSON":
      return "json";
    default:
      return "bin";
  }
}

function createDateStamp(): string {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1,
  ).padStart(2, "0");
  const day = String(
    date.getDate(),
  ).padStart(2, "0");
  const hour = String(
    date.getHours(),
  ).padStart(2, "0");
  const minute = String(
    date.getMinutes(),
  ).padStart(2, "0");

  return `${year}${month}${day}-${hour}${minute}`;
}

function formatReportCode(input: string): string {
  return input
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) => character.toUpperCase(),
    );
}

function stringValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}